// app/components/Plugins/Automations/nodes/implementations/StartNodeImpl.ts

import { BaseNode } from '../../core/BaseNode';
import { ExecutionContext } from '../../core/protocols';
import { AutomationNode, StartNodeData } from '../../types';

/**
 * Implementation of the Start Node protocol
 * Responsible for initializing flow execution with ticker symbols
 * and handling recurring execution based on frequency settings
 */
export class StartNodeImpl extends BaseNode {
  private timerId: NodeJS.Timeout | null = null;
  
  constructor(node: AutomationNode) {
    super(node);
  }

  /**
   * Process the start node
   * Extracts tickers from the node data and initializes the execution context
   * Sets up recurring execution if frequency is not 'once'
   */
  async process(context: ExecutionContext): Promise<boolean> {
    // Get tickers from node data
    const startNodeData = this.data as StartNodeData;
    const tickersString = startNodeData.tickers || '';
    const tickers = tickersString.split(',').map(t => t.trim()).filter(Boolean);
    
    if (tickers.length === 0) {
      context.addLog('Error: No tickers specified');
      return false;
    }
    
    // Set the current ticker in the context
    context.ticker = tickers[0]; // For now, just use the first ticker
    context.addLog(`Starting automation with ticker: ${context.ticker}`);
    
    // Store all tickers in the context data for potential use by other nodes
    context.data.tickers = tickers;
    
    // Set the node as running for UI updates
    startNodeData.isRunning = true;
    
    // Handle recurring execution based on frequency
    const frequency = startNodeData.frequency || 'once';
    
    if (frequency !== 'once') {
      // Calculate seconds for the next run
      let seconds = 0;
      switch (frequency) {
        case '1m': seconds = 60; break;
        case '5m': seconds = 5 * 60; break;
        case '15m': seconds = 15 * 60; break;
        case '30m': seconds = 30 * 60; break;
        case '1hr': seconds = 60 * 60; break;
        case 'custom': seconds = startNodeData.customSeconds || 60; break;
      }
      
      // Set initial countdown
      startNodeData.countdown = seconds;
      
      // Store flow execution info in context for recurring execution
      context.data.recurringExecution = {
        startNodeId: this.id,
        frequency,
        seconds,
        tickers: tickersString // Store the original tickers string for reuse
      };
      
      context.addLog(`Flow will execute again in ${seconds} seconds`);
    } else {
      // Ensure countdown is reset for non-recurring executions
      startNodeData.countdown = 0;
    }
    
    return true;
  }

  /**
   * Set up recurring execution timer
   * This method would be called by the execution engine after flow completion
   */
  setupRecurringExecution(callback: () => void, seconds: number): void {
    // Clear any existing timer
    if (this.timerId) {
      clearTimeout(this.timerId);
    }
    
    // Set up new timer
    this.timerId = setTimeout(() => {
      // Reset the timer
      this.timerId = null;
      
      // Call the callback to restart flow execution
      callback();
    }, seconds * 1000);
    
    // Update the node data for UI updates
    const startNodeData = this.data as StartNodeData;
    startNodeData.countdown = seconds;
  }

  /**
   * Cancel recurring execution
   * This method would be called when the flow is stopped manually
   */
  cancelRecurringExecution(): void {
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
    
    // Update the node data for UI updates
    const startNodeData = this.data as StartNodeData;
    startNodeData.isRunning = false;
    startNodeData.countdown = 0;
  }

  /**
   * Validate the start node configuration
   */
  validate() {
    const result = super.validate();
    const errors = [...result.errors];
    
    // Check if tickers are specified
    const tickersString = (this.data as StartNodeData).tickers || '';
    if (!tickersString.trim()) {
      errors.push('No tickers specified');
    }
    
    // Validate custom seconds if frequency is custom
    const startNodeData = this.data as StartNodeData;
    if (startNodeData.frequency === 'custom' && (!startNodeData.customSeconds || startNodeData.customSeconds < 1)) {
      errors.push('Custom seconds must be at least 1');
    }
    
    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Get the input ports for this node
   * Start node has no inputs
   */
  getInputs(): string[] {
    return [];
  }

  /**
   * Get the output ports for this node
   * Start node has one output
   */
  getOutputs(): string[] {
    return ['out'];
  }
}